// 招牌①「作品浮出」（2026-09-25 三站動態設計 Phase B；規格 SSOT＝vault《三站動態設計》§5.1）
//
// 點作品卡（Works 列表、首頁精選、作品頁的相關作品）→ 那一張縮圖形變成作品頁主視覺：760ms cubic-bezier(.4,.14,.3,1)。
// 只替被點的那一張命名 view-transition-name: work-media（pointerdown／Enter 時記住，beforeResolve 確認目的地相符、
// 卡在視窗內才命名）；抵達端 WorkRecord 的 .vt-work-media（封面圖，或頁首影片外框＋墊底圖）命名同一個名字。
// 兩張快照不互相淡換：抵達端先墊同一張封面（已在快取），所以新快照一開始就長得跟縮圖一樣。
// 形變過 55% 後，標題以 GSAP SplitText 逐行遮罩上浮（每行 700ms、錯開 60ms），動完 revert() 還原 DOM；
// 導言與側欄再晚 120ms 淡入。返回：作品頁主視覺命名、列表捲動還原後找回那張卡命名，反向形變 600ms。
// 只在前進／返回的 client 端執行；預渲染、爬蟲、減少動態都不跑（減少動態根本不走 View Transitions）。
import { addTransitionHook, isBot } from './view-transition'

// 作品與展覽主頁（/work/）：整塊的標題連結（a.sc-stretch）、文字連結（a.sc-go）、系列專區的圖（a.sc-pic）、頁首輪播（a.sc-hero-slide）
const CARD = 'a.archive-card, a.selected-work, .work-related-grid > a, a.sc-stretch, a.sc-go, a.sc-pic, a.sc-hero-slide'
// 卡片自己沒有圖（標題、文字連結）時，用同一筆（article／figure／section）裡的第一張圖
const cardImg = card => card?.querySelector('img') || card?.closest('article, figure, .sc-series')?.querySelector('img') || null
let pressed = null
let named = []
let active = null   // 目前這次轉場（新轉場打斷舊的時，舊的 after 不能清掉新的命名）
let gsapModules = null

const stripLocale = path => path.replace(/^\/zh(?=\/|$)/, '').replace(/\/+$/, '')
const workSlug = path => stripLocale(path).match(/^\/work\/([^/?#]+)$/)?.[1] || null
const isWorkDetail = route => route?.meta?.publicEntry?.component === 'pageWorkDetail'

function visibleRatio(el) {
  const r = el.getBoundingClientRect()
  if (!r.width || !r.height || r.right <= 0 || r.left >= innerWidth) return 0
  return Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0)) / r.height
}
function name(el) { el.style.viewTransitionName = 'work-media'; named.push(el) }
function clearNames() {
  for (const el of named) el.style.viewTransitionName = ''
  named = []
  delete document.documentElement.dataset.vtMedia
}

function remember(event) {
  if (event.type === 'keydown' && event.key !== 'Enter') return
  const card = event.target?.closest?.(CARD)
  const img = cardImg(card)
  pressed = card && img ? { card, img, at: performance.now() } : null
}

// 標題逐行遮罩：新畫面拍照之前先切好、藏在遮罩下；轉場動畫開始後 418ms（形變 55%）起跑
function prepareTitle(entry) {
  if (!gsapModules) return
  const { gsap, SplitText } = gsapModules
  const main = document.querySelector('#main-content')
  const h1 = main?.querySelector('.work-identity h1')
  if (!h1) return
  const cs = getComputedStyle(h1)
  if (document.fonts && !document.fonts.check(`${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`)) return   // 字型未到：不切（寧可不做逐行，也不要切錯行）
  let split
  try { split = SplitText.create(h1, { type: 'lines', mask: 'lines', linesClass: 'vt-line' }) } catch { return }
  const later = [...main.querySelectorAll('.work-reading-layout')].slice(0, 1)
  gsap.set(split.lines, { yPercent: 105 })
  gsap.set(later, { opacity: 0 })
  const restore = () => { split.revert(); gsap.set(later, { clearProps: 'opacity' }) }
  entry.vt.ready.then(() => {
    gsap.timeline({ delay: 0.418, onComplete: restore, onInterrupt: restore })
      .to(split.lines, { yPercent: 0, duration: 0.7, stagger: 0.06, ease: 'power4.out' })   // power4.out ＝ cubic-bezier(.22,1,.36,1)
      .to(later, { opacity: 1, duration: 0.28, ease: 'power2.out' }, 0.12)
  }, restore)
}

export function installWorkMorph() {
  if (typeof document === 'undefined' || isBot()) return
  document.addEventListener('pointerdown', remember, { capture: true, passive: true })
  document.addEventListener('keydown', remember, true)
  // gsap＋SplitText 不進主程式包：閒置時先載好，第一次開作品時就能用（還沒載好就只做形變、不做逐行）
  const idle = window.requestIdleCallback || (cb => setTimeout(cb, 1200))
  idle(() => Promise.all([import('gsap'), import('gsap/SplitText'),
    // 作品頁標題字型（英文 Rokkitt、中文 Noto Serif TC）：沒載好就切行，行會切錯（後備字型較寬）
    document.fonts?.load('500 64px Rokkitt').catch(() => {}), document.fonts?.load('500 56px "Noto Serif TC"').catch(() => {})]).then(([g, s]) => {
    const gsap = g.gsap || g.default
    gsap.registerPlugin(s.SplitText)
    gsapModules = { gsap, SplitText: s.SplitText }
  }).catch(() => {}))

  addTransitionHook({
    before(entry) {
      clearNames()
      active = entry
      const root = document.documentElement
      const toSlug = isWorkDetail(entry.to) && workSlug(entry.to.path)
      const fromSlug = isWorkDetail(entry.from) && workSlug(entry.from.path)
      const press = pressed && performance.now() - pressed.at < 4000 ? pressed : null
      pressed = null
      if (toSlug && entry.direction !== 'back' && press?.img.isConnected) {
        let href
        try { href = new URL(press.card.href).pathname } catch {}
        if (href && workSlug(href) === toSlug && visibleRatio(press.img) >= 0.3) {
          name(press.img)
          entry.media = 'open'
          root.dataset.vtMedia = 'open'
          root.dataset.nav = 'forward'   // 相關作品（作品頁 → 作品頁）也算往前
        }
      } else if (fromSlug && entry.direction === 'back') {
        const media = document.querySelector('#main-content .vt-work-media')
        if (media && visibleRatio(media) >= 0.2) {
          name(media)
          entry.media = 'close'
          entry.slug = fromSlug
          root.dataset.vtMedia = 'close'
        }
      }
    },
    ready(entry) {
      if (entry.media === 'open') {
        const target = document.querySelector('#main-content .vt-work-media')
        if (target) { name(target); prepareTitle(entry) }
      } else if (entry.media === 'close') {
        // 同一件作品在頁面上可能出現不只一次（/work/ 的年份段落）：挑畫面裡露出最多的那張
        let img = null, best = 0
        for (const a of document.querySelectorAll(CARD.split(',').map(sel => '#main-content ' + sel.trim()).join(','))) {
          let slug
          try { slug = workSlug(new URL(a.href).pathname) } catch { continue }
          if (slug !== entry.slug) continue
          const candidate = cardImg(a), ratio = candidate ? visibleRatio(candidate) : 0
          if (ratio > best) { best = ratio; img = candidate }
        }
        if (img && best >= 0.3) name(img)
      }
    },
    after(entry) { if (entry === active) { clearNames(); active = null } },
  })
}
